/**
 * Cinematic Moments Engine
 * Detects "WOW" moments from lightweight live signals and turns them into
 * shareable, throttled cinematic events. No DB writes — pure derivation.
 */

export type CinematicMomentKind =
  | "hype_spike"
  | "nation_dominance"
  | "prediction_explosion"
  | "momentum_swing"
  | "last_minute_drama"
  | "rivalry_surge"
  | "leaderboard_takeover"
  | "poll_consensus"
  | "community_shockwave";

export type CinematicMoment = {
  id: string;
  kind: CinematicMomentKind;
  emoji: string;
  headlineKey: string; // i18n key
  subKey?: string;     // i18n key
  tokens?: Record<string, string | number>;
  intensity: number;   // 0..1 — drives glow strength
  accent: "rose" | "amber" | "cyan" | "violet" | "emerald";
  createdAt: number;
};

export type CinematicSignals = {
  activeFans?: number;
  hypeBurst?: number;          // pulses in last minute
  topNation?: string | null;
  topNationShare?: number;     // 0..1 share of top vs total
  matchMinute?: number;
  pollConsensus?: number;      // 0..1 highest option share
  predictionsRate?: number;    // predictions/min
  momentum?: number;           // -1..1
};

const COOLDOWN_MS = 12_000;

function pickAccent(kind: CinematicMomentKind): CinematicMoment["accent"] {
  switch (kind) {
    case "hype_spike":
    case "community_shockwave":
      return "rose";
    case "nation_dominance":
    case "rivalry_surge":
      return "amber";
    case "prediction_explosion":
    case "poll_consensus":
      return "violet";
    case "momentum_swing":
    case "leaderboard_takeover":
      return "cyan";
    case "last_minute_drama":
      return "emerald";
  }
}

export class CinematicMomentsEngine {
  private lastByKind = new Map<CinematicMomentKind, number>();

  reset() { this.lastByKind.clear(); }

  detect(s: CinematicSignals, now: number = Date.now()): CinematicMoment | null {
    const candidates: Array<{ kind: CinematicMomentKind; intensity: number; tokens?: Record<string, string | number>; emoji: string; headlineKey: string; subKey?: string }> = [];

    if ((s.hypeBurst ?? 0) >= 30) {
      candidates.push({
        kind: "hype_spike",
        intensity: Math.min(1, (s.hypeBurst ?? 0) / 200),
        emoji: "💥",
        headlineKey: "cm_hype_spike_h",
        subKey: "cm_hype_spike_s",
        tokens: { n: s.hypeBurst ?? 0 },
      });
    }
    if (s.topNation && (s.topNationShare ?? 0) >= 0.45) {
      candidates.push({
        kind: "nation_dominance",
        intensity: Math.min(1, (s.topNationShare ?? 0)),
        emoji: "🔥",
        headlineKey: "cm_nation_dominance_h",
        subKey: "cm_nation_dominance_s",
        tokens: { nation: s.topNation, pct: Math.round((s.topNationShare ?? 0) * 100) },
      });
    }
    if ((s.pollConsensus ?? 0) >= 0.8) {
      candidates.push({
        kind: "poll_consensus",
        intensity: s.pollConsensus ?? 0,
        emoji: "👀",
        headlineKey: "cm_poll_consensus_h",
        subKey: "cm_poll_consensus_s",
        tokens: { pct: Math.round((s.pollConsensus ?? 0) * 100) },
      });
    }
    if ((s.predictionsRate ?? 0) >= 50) {
      candidates.push({
        kind: "prediction_explosion",
        intensity: Math.min(1, (s.predictionsRate ?? 0) / 200),
        emoji: "🎯",
        headlineKey: "cm_prediction_explosion_h",
        subKey: "cm_prediction_explosion_s",
      });
    }
    if (Math.abs(s.momentum ?? 0) >= 0.75) {
      candidates.push({
        kind: "momentum_swing",
        intensity: Math.abs(s.momentum ?? 0),
        emoji: "⚡",
        headlineKey: "cm_momentum_swing_h",
        subKey: "cm_momentum_swing_s",
      });
    }
    if ((s.matchMinute ?? 0) >= 85) {
      candidates.push({
        kind: "last_minute_drama",
        intensity: Math.min(1, ((s.matchMinute ?? 0) - 80) / 15),
        emoji: "⏱️",
        headlineKey: "cm_last_minute_h",
        subKey: "cm_last_minute_s",
      });
    }
    if ((s.activeFans ?? 0) >= 5000) {
      candidates.push({
        kind: "community_shockwave",
        intensity: Math.min(1, (s.activeFans ?? 0) / 50000),
        emoji: "🌎",
        headlineKey: "cm_shockwave_h",
        subKey: "cm_shockwave_s",
        tokens: { n: s.activeFans ?? 0 },
      });
    }

    if (candidates.length === 0) return null;

    // pick highest-intensity candidate respecting per-kind cooldown
    candidates.sort((a, b) => b.intensity - a.intensity);
    for (const c of candidates) {
      const last = this.lastByKind.get(c.kind) ?? 0;
      if (now - last < COOLDOWN_MS) continue;
      this.lastByKind.set(c.kind, now);
      return {
        id: `${c.kind}-${now}`,
        kind: c.kind,
        emoji: c.emoji,
        headlineKey: c.headlineKey,
        subKey: c.subKey,
        tokens: c.tokens,
        intensity: c.intensity,
        accent: pickAccent(c.kind),
        createdAt: now,
      };
    }
    return null;
  }
}